import { integrationApi } from '@/api/integrations';
import { useCurrency } from '@/hooks/useCurrency';
import {
    ArrowLeftIcon,
    CheckIcon,
    PhotoIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  padding: 2rem;
  max-width: 1560px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  color: var(--gray-700);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--gray-50);
    border-color: var(--gray-400);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--gray-900);
`;

const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }

  &:disabled {
    background: var(--gray-100);
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--gray-300);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--gray-900);
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
  }
`;

const ImageUploadArea = styled.div`
  border: 2px dashed var(--gray-300);
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-600);
    background: var(--primary-50);
  }
`;

const ImageUploadIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0.75rem;

  svg {
    width: 3rem;
    height: 3rem;
    color: var(--gray-400);
  }
`;

const ImageUploadText = styled.div`
  font-size: 0.875rem;
  color: var(--gray-600);
  margin-bottom: 0.25rem;
`;

const ImageUploadHint = styled.div`
  font-size: 0.75rem;
  color: var(--gray-500);
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const ImagePreview = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 0.5rem;
  overflow: hidden;
  background: var(--gray-100);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 1.75rem;
  height: 1.75rem;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const InputWithAddon = styled.div`
  position: relative;
  display: flex;
`;

const InputAddon = styled.span`
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-500);
  font-size: 0.875rem;
  pointer-events: none;
`;

const InputWithPadding = styled(Input)`
  padding-left: 2rem;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--primary-100);
  color: var(--primary-700);
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;

  button {
    background: none;
    border: none;
    color: var(--primary-700);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

const MarketplacesSection = styled.div`
  margin-top: 1rem;
`;

const MarketplaceItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border: 1px solid var(--gray-200);
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s;

  &:hover {
    border-color: var(--gray-300);
    background: var(--gray-50);
  }
`;

const MarketplaceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const MarketplaceLogo = styled.div<{ $color: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: ${(props) => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: white;
  }
`;

const MarketplaceDetails = styled.div``;

const MarketplaceName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.125rem;
`;

const MarketplaceType = styled.div`
  font-size: 0.75rem;
  color: var(--gray-600);
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--gray-300);
    transition: 0.3s;
    border-radius: 24px;

    &:before {
      position: absolute;
      content: '';
      height: 18px;
      width: 18px;
      left: 3px;
      bottom: 3px;
      background: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }

  input:checked + span {
    background: var(--primary-600);
  }

  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const StatusSection = styled.div``;

const StatusOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--gray-200);
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: var(--primary-600);
    background: var(--primary-50);
  }

  input {
    width: 1.25rem;
    height: 1.25rem;
    cursor: pointer;
  }
`;

const StatusLabel = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-900);
  margin-bottom: 0.125rem;
`;

const StatusDescription = styled.div`
  font-size: 0.75rem;
  color: var(--gray-600);
`;

const ActionsBar = styled.div`
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid var(--gray-200);
  padding: 1rem 2rem;
  margin: 0 -2rem -2rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.625rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  ${(props) => {
    if (props.$variant === 'danger') {
      return `
        background: var(--error-600);
        color: white;
        
        &:hover {
          background: var(--error-700);
        }
      `;
    } else if (props.$variant === 'secondary') {
      return `
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        
        &:hover {
          background: var(--gray-50);
        }
      `;
    } else {
      return `
        background: var(--primary-600);
        color: white;
        
        &:hover {
          background: var(--primary-700);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
        }
      `;
    }
  }}
`;

interface Marketplace {
  id: string;
  name: string;
  type: string;
  color: string;
  enabled: boolean;
}

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const { symbol: currencySymbol } = useCurrency();

  const [formData, setFormData] = useState({
    name: isEditMode ? 'Premium Wireless Headphones' : '',
    sku: isEditMode ? 'WH-2024-001' : '',
    description: isEditMode
      ? 'High-quality wireless headphones with active noise cancellation, 30-hour battery life, and premium sound quality.'
      : '',
    category: isEditMode ? 'electronics' : '',
    price: isEditMode ? '129.99' : '',
    compareAtPrice: isEditMode ? '179.99' : '',
    cost: isEditMode ? '65.00' : '',
    quantity: isEditMode ? '150' : '',
    status: isEditMode ? 'published' : 'draft',
  });

  const [tags, setTags] = useState<string[]>(
    isEditMode ? ['wireless', 'audio', 'premium'] : []
  );
  const [tagInput, setTagInput] = useState('');

  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);

  useEffect(() => {
    integrationApi.listAll().then((categories) => {
      const active = categories
        .flatMap((cat) => cat.integrations)
        .filter((mod) => mod.is_connected)
        .map((mod) => ({
          id: mod.slug,
          name: mod.name,
          type: mod.name,
          color: mod.color,
          enabled: false,
        }));
      setMarketplaces(active);
    });
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleMarketplaceToggle = (marketplaceId: string) => {
    setMarketplaces(
      marketplaces.map((mp) =>
        mp.id === marketplaceId ? { ...mp, enabled: !mp.enabled } : mp
      )
    );
  };

  const handleSave = () => {
    console.log(
      'Saving product:',
      formData,
      tags,
      marketplaces.filter((mp) => mp.enabled)
    );
    navigate('/products');
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/products')}>
          <ArrowLeftIcon />
          Back to Products
        </BackButton>
        <Title>{isEditMode ? 'Edit Product' : 'Create New Product'}</Title>
      </Header>

      <FormLayout>
        <MainColumn>
          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <FormGroup>
              <Label>Product Name *</Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter product name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your product..."
              />
            </FormGroup>
            <FormGroup>
              <Label>SKU</Label>
              <Input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                placeholder="e.g., WH-2024-001"
              />
            </FormGroup>
            <FormGroup>
              <Label>Category</Label>
              <Select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select a category</option>
                <option value="electronics">Electronics</option>
                <option value="clothing">Clothing</option>
                <option value="home">Home & Garden</option>
                <option value="sports">Sports & Outdoors</option>
                <option value="toys">Toys & Games</option>
                <option value="books">Books</option>
              </Select>
            </FormGroup>
          </Section>

          <Section>
            <SectionTitle>Product Images</SectionTitle>
            <ImageUploadArea>
              <ImageUploadIcon>
                <PhotoIcon />
              </ImageUploadIcon>
              <ImageUploadText>
                Click to upload or drag and drop
              </ImageUploadText>
              <ImageUploadHint>PNG, JPG, GIF up to 10MB</ImageUploadHint>
            </ImageUploadArea>
            {isEditMode && (
              <ImagePreviewGrid>
                <ImagePreview>
                  <PhotoIcon
                    style={{
                      width: '2rem',
                      height: '2rem',
                      color: 'var(--gray-400)',
                    }}
                  />
                  <RemoveImageButton>
                    <XMarkIcon />
                  </RemoveImageButton>
                </ImagePreview>
              </ImagePreviewGrid>
            )}
          </Section>

          <Section>
            <SectionTitle>Pricing</SectionTitle>
            <PriceGrid>
              <FormGroup>
                <Label>Price *</Label>
                <InputWithAddon>
                  <InputAddon>{currencySymbol}</InputAddon>
                  <InputWithPadding
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </InputWithAddon>
              </FormGroup>
              <FormGroup>
                <Label>Compare at Price</Label>
                <InputWithAddon>
                  <InputAddon>{currencySymbol}</InputAddon>
                  <InputWithPadding
                    type="number"
                    name="compareAtPrice"
                    value={formData.compareAtPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                  />
                </InputWithAddon>
              </FormGroup>
            </PriceGrid>
            <FormGroup>
              <Label>Cost per Item</Label>
              <InputWithAddon>
                <InputAddon>{currencySymbol}</InputAddon>
                <InputWithPadding
                  type="number"
                  name="cost"
                  value={formData.cost}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                />
              </InputWithAddon>
            </FormGroup>
          </Section>

          <Section>
            <SectionTitle>Inventory</SectionTitle>
            <FormGroup>
              <Label>Quantity *</Label>
              <Input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                placeholder="0"
              />
            </FormGroup>
          </Section>
        </MainColumn>

        <SideColumn>
          <Section>
            <SectionTitle>Status</SectionTitle>
            <StatusSection>
              <StatusOption>
                <input
                  type="radio"
                  name="status"
                  value="published"
                  checked={formData.status === 'published'}
                  onChange={handleInputChange}
                />
                <StatusLabel>
                  <StatusTitle>Published</StatusTitle>
                  <StatusDescription>
                    Product is live and visible to customers
                  </StatusDescription>
                </StatusLabel>
              </StatusOption>
              <StatusOption>
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === 'draft'}
                  onChange={handleInputChange}
                />
                <StatusLabel>
                  <StatusTitle>Draft</StatusTitle>
                  <StatusDescription>
                    Product is hidden from customers
                  </StatusDescription>
                </StatusLabel>
              </StatusOption>
              <StatusOption>
                <input
                  type="radio"
                  name="status"
                  value="archived"
                  checked={formData.status === 'archived'}
                  onChange={handleInputChange}
                />
                <StatusLabel>
                  <StatusTitle>Archived</StatusTitle>
                  <StatusDescription>
                    Product is archived and not visible
                  </StatusDescription>
                </StatusLabel>
              </StatusOption>
            </StatusSection>
          </Section>

          <Section>
            <SectionTitle>Tags</SectionTitle>
            {tags.length > 0 && (
              <TagsContainer>
                {tags.map((tag) => (
                  <Tag key={tag}>
                    {tag}
                    <button onClick={() => handleRemoveTag(tag)}>
                      <XMarkIcon />
                    </button>
                  </Tag>
                ))}
              </TagsContainer>
            )}
            <FormGroup>
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="Add tags (press Enter)"
              />
            </FormGroup>
          </Section>

          <Section>
            <SectionTitle>Marketplaces</SectionTitle>
            <MarketplacesSection>
              {marketplaces.length === 0 && (
                <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem', margin: 0 }}>
                  No active integrations. Connect a marketplace in{' '}
                  <a href="/settings?tab=integrations" style={{ color: 'var(--primary-600)' }}>Settings</a>.
                </p>
              )}
              {marketplaces.map((marketplace) => (
                <MarketplaceItem key={marketplace.id}>
                  <MarketplaceInfo>
                    <MarketplaceLogo $color={marketplace.color}>
                      <CheckIcon
                        style={{ opacity: marketplace.enabled ? 1 : 0 }}
                      />
                    </MarketplaceLogo>
                    <MarketplaceDetails>
                      <MarketplaceName>{marketplace.name}</MarketplaceName>
                      <MarketplaceType>{marketplace.type}</MarketplaceType>
                    </MarketplaceDetails>
                  </MarketplaceInfo>
                  <Toggle>
                    <input
                      type="checkbox"
                      checked={marketplace.enabled}
                      onChange={() => handleMarketplaceToggle(marketplace.id)}
                    />
                    <span></span>
                  </Toggle>
                </MarketplaceItem>
              ))}
            </MarketplacesSection>
          </Section>
        </SideColumn>
      </FormLayout>

      <ActionsBar>
        <Button $variant="secondary" onClick={() => navigate('/products')}>
          Cancel
        </Button>
        {isEditMode && <Button $variant="danger">Delete Product</Button>}
        <Button onClick={handleSave}>
          {isEditMode ? 'Save Changes' : 'Create Product'}
        </Button>
      </ActionsBar>
    </Container>
  );
};

export default ProductForm;
